/**
 * A hook for lazy loading remote modules using Module Federation.
 *
 * This hook allows you to dynamically load components or modules from other applications
 * that are exposed through Module Federation. It handles loading states and errors
 * by providing a fallback value while loading or if the remote module fails to load.
 *
 * @example
 * // Loading a React component
 * const RemoteButton = useLazyRemoteModule({
 *   manifestUrl: 'https://remote-app.example.com/remoteEntry.json',
 *   modulePath: './Button',
 *   fallback: { default: DefaultButton }
 * });
 *
 * // Loading a utility module
 * const utils = useLazyRemoteModule({
 *   manifestUrl: 'https://remote-app.example.com/remoteEntry.json',
 *   modulePath: './utils',
 *   fallback: { default: defaultUtils }
 * });
 *
 * @param options
 * @param options.manifestUrl - The URL to the remote app's Module Federation manifest JSON
 * @param options.modulePath - The path to the module as defined in the remote app's Module Federation config
 * @param options.fallback - The value to use while loading or if loading fails. Should be an object with a 'default' key to match module format
 * @param options.skip - Optional flag to skip loading the remote module
 *
 * @returns The loaded remote module if successful, otherwise the fallback value
 *
 * @note
 * - The remote app must expose the module through its Module Federation config
 * - The fallback should be an object with a 'default' key to match the module format
 * - The hook will automatically handle reloading if the manifest URL, module, or scope changes
 * - If loading fails or returns 'fallback', the fallback value will be used
 * - The hook requires Module Federation to be initialized before use
 * - Every new load attempt (a manifestUrl/modulePath/scope/skip change) resets the returned value
 *   to `fallback` before fetching, so switching to a target with no module -- or one whose fetch
 *   fails -- never keeps rendering whatever the *previous* target's module resolved to
 */

import { useEffect, useState } from 'react';

import { loadRemote } from '@module-federation/runtime';
import { getLogger } from 'src/utils/datadog-logger';
import { ensureRemoteInitialized } from '../runtime-init';

const REMOTE_LOAD_TIMEOUT_MS = 4_000;
const logger = getLogger('lazy-remote-module');

type UseLazyRemoteModuleProps<T> = {
  fallback: T;
  modulePath: string;
  manifestUrl: string;
  onLoadFail?: (error: string) => void;
  onLoadRecovered?: () => void;
  onLoading?: (isLoading: boolean) => void;
  scope?: string;
  skip?: boolean;
};

function useLazyRemoteModule<T>({
  fallback,
  modulePath,
  manifestUrl,
  scope = '@dutchie/customer-themes',
  skip,
  onLoadFail,
  onLoadRecovered,
  onLoading,
}: UseLazyRemoteModuleProps<T>): T {
  const [remoteModule, setRemoteModule] = useState<T>(fallback);

  useEffect(() => {
    // `setRemoteModule` below was previously write-only on the success path: every other exit
    // (skip, MF uninitialized, registration failure, timeout, a 'fallback' result, a thrown
    // error) left whatever module a *previous* load attempt had resolved to untouched. That
    // turned this into a one-way ratchet -- once dispensary A's themed module loaded, switching
    // the "previewing as" selection to dispensary B (no theme, or a modulePath that fails to
    // resolve against the shared manifest) kept rendering dispensary A's SDK components
    // indefinitely, since nothing ever reset the state back to `fallback`. Resetting here, at the
    // top of every effect run, only fires on a genuine identity change (this effect's deps are
    // exactly the parameters of "what to load"), so it can't fire on an unrelated re-render.
    setRemoteModule(fallback);

    if (skip) {
      // Release any pending hold in the caller — we won't be fetching.
      onLoading?.(false);
      return;
    }
    if (!window.__MF_INITIALIZED) {
      console.log('MF not initialized');
      onLoading?.(false);
      return;
    }
    const initialized = ensureRemoteInitialized({
      manifestUrl,
      name: scope,
    });
    if (!initialized) {
      onLoading?.(false);
      return;
    }

    let timedOut = false;
    let cancelled = false;
    const startedAt = Date.now();

    const timeoutId = setTimeout(() => {
      timedOut = true;
      logger.warn('Remote module load timed out', {
        manifestUrl,
        modulePath,
        timeoutMs: REMOTE_LOAD_TIMEOUT_MS,
      });
      onLoadFail?.(`Remote module ${modulePath} timed out after ${REMOTE_LOAD_TIMEOUT_MS}ms`);
      onLoading?.(false);
    }, REMOTE_LOAD_TIMEOUT_MS);

    onLoading?.(true);
    loadRemote(`${scope}/${modulePath}`)
      .then((result: T | 'fallback') => {
        if (cancelled) return;
        if (result === 'fallback') {
          if (!timedOut) {
            onLoadFail?.(`Failed to load remote module ${modulePath}`);
          }
          return;
        }
        setRemoteModule(result);
        if (timedOut) {
          logger.warn('Remote module loaded after timeout', {
            elapsedMs: Date.now() - startedAt,
            manifestUrl,
            modulePath,
          });
          onLoadRecovered?.();
        }
      })
      .catch(() => {
        if (cancelled || timedOut) return;
        console.log('error loading remote module', modulePath);
        onLoadFail?.(`Failed to load remote module ${modulePath}`);
      })
      .finally(() => {
        if (!cancelled && !timedOut) {
          clearTimeout(timeoutId);
          onLoading?.(false);
        }
      });

    // eslint-disable-next-line consistent-return
    return () => {
      // React runs this cleanup before the next effect's body, so a stale promise from an
      // abandoned target can never write into a later switch's state (tested in index.test.ts).
      cancelled = true;
      clearTimeout(timeoutId);
    };
    // `fallback` is intentionally excluded below: callers pass a fresh object literal per render
    // (see remote-theme.tsx's `{ default: undefined }`), so including it would re-run this
    // effect -- and re-fetch -- on every unrelated render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [manifestUrl, modulePath, scope, skip, onLoadFail, onLoadRecovered, onLoading]);

  return remoteModule;
}

export default useLazyRemoteModule;
